import { isProposalDeliveryComplete } from '@bcube/shared/constants';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { cartItemApi } from '@/api/commercial/cart-items';
import { ContractApiError, contractApi } from '@/api/commercial/contracts';
import { ProposalStatus, proposalApi } from '@/api/commercial/proposals';
import { StepNavigation } from '@/components/commercial/accounts/create/step-navigation';
import {
  type ProposalContractViewModel,
  toProposalContractViewModel,
} from '@/components/commercial/proposals/contracts/proposal-contract-types';
import { ProposalContractsDialog } from '@/components/commercial/proposals/contracts/proposal-contracts-dialog';
import { StepConfigurations } from '@/components/commercial/proposals/create/configurations/step-configurations';
import { ProposalStatusBadge } from '@/components/commercial/proposals/create/proposal-status-badge';
import { ResumeHeaderActions } from '@/components/commercial/proposals/create/resume/resume-actions-panel';
import { StepCart } from '@/components/commercial/proposals/create/step-cart';
import {
  StepCartFooterActions,
  StepCartHeaderActions,
} from '@/components/commercial/proposals/create/step-cart-actions';
import { StepDelivery } from '@/components/commercial/proposals/create/step-delivery';
import { StepResume } from '@/components/commercial/proposals/create/step-resume';
import { StepClientUpdateContainer } from '@/components/commercial/proposals/update/step-client-container';
import { StepCreditAnalysisUpdate } from '@/components/commercial/proposals/update/step-credit-analysis';
import { UpdateProposalProvider, useUpdateProposal } from '@/context/update-proposal-context';
import {
  type PaymentFormState,
  useCartStepValidation,
} from '@/hooks/commercial/use-cart-step-validation';
import { useProposalContracts, useSyncProposalContracts } from '@/hooks/commercial/use-contracts';
import { useProposalConfigurations } from '@/hooks/commercial/use-proposal-configurations';
import { useProposalDeliveryGuard } from '@/hooks/commercial/use-proposal-delivery-guard';
import { useProposalStepProgress } from '@/hooks/use-proposal-step-progress';
import { toast } from '@/hooks/use-toast';
import { downloadBlob, openBlobInTab, openEmptyTab } from '@/lib/utils';

const STEP_IDS = ['client', 'credit-analysis', 'cart', 'config', 'delivery', 'resume'] as const;

function errorMessage(error: unknown): string {
  return error instanceof ContractApiError
    ? error.message
    : 'Não foi possível concluir a operação.';
}

function ProposalUpdateContent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentStep = searchParams.get('step') || 'client';

  const {
    data: { proposal, account },
    isLoading,
    error,
  } = useUpdateProposal();

  const [isDeliverySaving, setIsDeliverySaving] = useState(false);
  const [draft, setDraft] = useState<{ proposalId?: string; payment: PaymentFormState }>();
  const [isSaving, setIsSaving] = useState(false);
  const [contractsDialogOpen, setContractsDialogOpen] = useState(false);
  const syncedCartSignatureRef = useRef<string | null>(null);

  const savePaymentFnRef = useRef<(() => Promise<void>) | null>(null);
  const stepNavigationInFlightRef = useRef(false);
  const isCartStep = currentStep === 'cart';
  const isResumeStep = currentStep === 'resume';

  const proposalId = proposal?.id;
  const cartValidation = useCartStepValidation({
    proposalId,
    draftPayment: isCartStep && draft?.proposalId === proposalId ? draft?.payment : undefined,
  });
  const handlePaymentStateChange = useCallback(
    (payment: PaymentFormState | undefined) => {
      setDraft(payment ? { proposalId, payment } : undefined);
    },
    [proposalId],
  );
  const { data: remoteProposal } = useQuery({
    queryKey: ['proposal', proposalId],
    queryFn: () => proposalApi.findOne(proposalId!),
    enabled: !!proposalId,
  });
  const cartItemsQuery = useQuery({
    queryKey: ['cart-items', proposalId],
    queryFn: () => cartItemApi.findMany(proposalId ?? ''),
    enabled: Boolean(proposalId),
  });
  const { data: proposalConfigurations } = useProposalConfigurations(proposalId);
  const isConfigurationStepValid = proposalConfigurations?.isValidated ?? false;
  const deliveryProposal = remoteProposal ?? proposal;
  const isDeliveryComplete = isProposalDeliveryComplete(deliveryProposal);
  const redirectDeliveryStep = useCallback(
    (step: string) => setSearchParams({ step }, { replace: true }),
    [setSearchParams],
  );
  const deliveryGuard = useProposalDeliveryGuard({
    currentStep,
    proposalId,
    isCartValid: cartValidation.isValid,
    isConfigurationValid: isConfigurationStepValid,
    isDeliveryComplete,
    isLoading: cartValidation.isLoading || (!!proposalId && !remoteProposal),
    redirect: redirectDeliveryStep,
  });

  const steps = useProposalStepProgress({
    proposal,
    isDeliveryComplete,
    isCartStepValid: cartValidation.isValid,
    isConfigurationStepValid,
  });

  const contractStatuses = new Set<ProposalStatus>([
    ProposalStatus.OPEN,
    ProposalStatus.PENDING_ACCEPT,
    ProposalStatus.EXPIRED,
    ProposalStatus.REFUSED,
  ]);
  const hasValidatedContractSteps = Boolean(
    proposal?.id && (cartItemsQuery.data?.length ?? 0) > 0 && isDeliveryComplete,
  );
  const canShowContractAction = Boolean(
    proposal?.status &&
      contractStatuses.has(proposal.status as ProposalStatus) &&
      hasValidatedContractSteps,
  );
  const proposalContractsQuery = useProposalContracts(proposalId, canShowContractAction);
  const {
    mutate: syncProposalContracts,
    error: syncContractsError,
    isPending: isSyncingContracts,
  } = useSyncProposalContracts();
  const proposalContracts = (proposalContractsQuery.data ?? []).map(toProposalContractViewModel);
  const cartSignature = (cartItemsQuery.data ?? [])
    .map((item) => `${item.id}:${item.negotiationType}:${item.productId}`)
    .sort()
    .join('|');

  useEffect(() => {
    if (!proposalId || !canShowContractAction || !cartSignature) return;
    if (syncedCartSignatureRef.current === cartSignature) return;
    syncedCartSignatureRef.current = cartSignature;
    syncProposalContracts(proposalId);
  }, [canShowContractAction, cartSignature, proposalId, syncProposalContracts]);

  const contractsError = syncContractsError ?? proposalContractsQuery.error;
  const contractsUnavailableReason = contractsError
    ? contractsError instanceof ContractApiError
      ? contractsError.message
      : 'Não foi possível identificar os contratos da proposta.'
    : proposalContracts.length === 0
      ? 'Nenhum contrato aplicável está vinculado à proposta.'
      : undefined;

  useEffect(() => {
    if (error) {
      navigate('/commercial/proposal');
    }
  }, [error, navigate]);

  const handleSavePaymentRef = useCallback((saveFn: (() => Promise<void>) | null) => {
    savePaymentFnRef.current = saveFn;
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await savePaymentFnRef.current?.();
      if (proposalId) {
        await queryClient.invalidateQueries({
          queryKey: ['cart-calculate', proposalId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['proposal', proposalId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['proposal-configurations', proposalId],
        });
      }
      toast({ variant: 'success', title: 'Proposta salva com sucesso' });
    } catch {
      toast({ title: 'Erro ao salvar proposta', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  }, [proposalId, queryClient]);

  const handleNextStep = useCallback(async () => {
    if (stepNavigationInFlightRef.current || isDeliverySaving) return;
    stepNavigationInFlightRef.current = true;
    setIsSaving(true);
    try {
      await savePaymentFnRef.current?.();
      if (proposalId) {
        await queryClient.invalidateQueries({
          queryKey: ['cart-calculate', proposalId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['proposal', proposalId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['proposal-configurations', proposalId],
        });
      }
      const currentIdx = STEP_IDS.indexOf(currentStep as (typeof STEP_IDS)[number]);
      const nextStep = STEP_IDS[currentIdx + 1];
      if (nextStep) {
        setSearchParams({ step: nextStep }, { replace: true });
      }
    } catch {
      toast({ title: 'Erro ao salvar proposta', variant: 'destructive' });
    } finally {
      stepNavigationInFlightRef.current = false;
      setIsSaving(false);
    }
  }, [currentStep, proposalId, queryClient, setSearchParams, isDeliverySaving]);

  const handleStepNavigate = useCallback(async () => {
    if (stepNavigationInFlightRef.current || isDeliverySaving) return false;
    if (currentStep !== 'cart') return true;

    stepNavigationInFlightRef.current = true;
    setIsSaving(true);
    try {
      await savePaymentFnRef.current?.();
      return true;
    } catch {
      toast({ title: 'Erro ao salvar proposta', variant: 'destructive' });
      return false;
    } finally {
      stepNavigationInFlightRef.current = false;
      setIsSaving(false);
    }
  }, [currentStep, isDeliverySaving]);

  const handleResumeUpdate = useCallback(() => {
    if (proposal?.id) {
      queryClient.invalidateQueries({ queryKey: ['proposal', proposal.id] });
    }
  }, [proposal?.id, queryClient]);

  const handleReopenProposal = useCallback(() => {
    setSearchParams({ step: 'client' }, { replace: true });
  }, [setSearchParams]);

  const handleViewContract = useCallback(async (contract: ProposalContractViewModel) => {
    const previewWindow = openEmptyTab();
    if (!previewWindow) {
      toast({
        title: 'Não foi possível abrir o contrato',
        description: 'Permita pop-ups para visualizar o PDF em uma nova aba.',
        variant: 'destructive',
      });
      return;
    }
    try {
      const file = await contractApi.getPdf(contract.id, contract.version, 'inline');
      openBlobInTab(file.blob, previewWindow);
    } catch (pdfError) {
      previewWindow.close();
      toast({
        title: 'Erro ao visualizar contrato',
        description: errorMessage(pdfError),
        variant: 'destructive',
      });
    }
  }, []);

  const handleDownloadContract = useCallback(async (contract: ProposalContractViewModel) => {
    try {
      const file = await contractApi.getPdf(contract.id, contract.version, 'attachment');
      downloadBlob(file.blob, file.fileName);
    } catch (pdfError) {
      toast({
        title: 'Erro ao baixar contrato',
        description: errorMessage(pdfError),
        variant: 'destructive',
      });
    }
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // V-FE-04: Page title should reflect "Proposta [ID] – [Razão Social]"
  const pageTitle = proposal
    ? `Proposta ${proposal.seq} - ${proposal.clientName || account?.social_name || account?.name || 'Cliente'}`
    : 'Editar Proposta';

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div
        className={`flex justify-between gap-4 ${isCartStep ? 'flex-nowrap items-start' : 'flex-wrap items-center'}`}
      >
        <div className="min-w-0 flex-1 break-words">
          {isResumeStep ? (
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-semibold">{pageTitle}</h1>
              <ProposalStatusBadge status={proposal?.status} />
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-semibold">{pageTitle}</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Edite os dados da proposta comercial
              </p>
            </>
          )}
        </div>
        {isCartStep && (
          <StepCartHeaderActions
            onCancel={() => navigate('/commercial/proposal')}
            onSave={handleSave}
            isSaving={isSaving}
          />
        )}
        {isResumeStep && deliveryGuard && (
          <ResumeHeaderActions
            proposalId={proposal?.id}
            status={proposal?.status}
            sentAt={proposal?.sentAt}
            onSave={handleSave}
            isSaving={isSaving}
            onUpdate={handleResumeUpdate}
            onReopen={handleReopenProposal}
            contracts={proposalContracts}
            showContractsAction={canShowContractAction}
            contractsUnavailableReason={contractsUnavailableReason}
            onViewContracts={() => setContractsDialogOpen(true)}
            onViewContract={handleViewContract}
            onDownloadContract={handleDownloadContract}
          />
        )}
      </div>

      {/* Step Navigation */}
      <div className="w-full max-w-[1400px] mx-auto mb-6">
        <StepNavigation
          steps={steps}
          onStepNavigate={handleStepNavigate}
          isNavigating={isSaving || isDeliverySaving}
        />
      </div>

      {/* Step Content */}
      <div className="w-full max-w-[1400px] mx-auto">
        {currentStep === 'client' && <StepClientUpdateContainer proposalId={proposal?.id || ''} />}
        {currentStep === 'credit-analysis' && (
          <StepCreditAnalysisUpdate proposalId={proposal?.id || ''} />
        )}
        {isCartStep && (
          <>
            <StepCart
              isPickup={deliveryProposal?.deliveryMode === 'BWS'}
              proposalId={proposal?.id}
              clientId={proposal?.clientId}
              savedPayment={remoteProposal?.payment ?? proposal?.payment}
              onPaymentStateChange={handlePaymentStateChange}
              onSavePaymentRef={handleSavePaymentRef}
            />
            <StepCartFooterActions
              onNextStep={handleNextStep}
              isStepValid={cartValidation.isValid}
              validationHint={cartValidation.message}
              isSaving={isSaving}
            />
          </>
        )}
        {currentStep === 'config' && (
          <>
            <StepConfigurations proposalId={proposal?.id} />
            <StepCartFooterActions
              onNextStep={handleNextStep}
              isStepValid={!!proposalId && cartValidation.isValid}
              validationHint={cartValidation.message}
              isSaving={isSaving}
            />
          </>
        )}
        {currentStep === 'delivery' && deliveryGuard && (
          <>
            <StepDelivery
              accountId={proposal?.clientId}
              proposalId={proposal?.id}
              proposal={deliveryProposal}
              onSavingChange={setIsDeliverySaving}
            />
            <StepCartFooterActions
              onNextStep={handleNextStep}
              isStepValid={isDeliveryComplete && !isDeliverySaving}
              validationHint={isDeliveryComplete ? null : 'Selecione o destino da proposta'}
              isSaving={isSaving}
            />
          </>
        )}
        {currentStep === 'resume' && deliveryGuard && (
          <StepResume
            proposalId={proposal?.id}
            clientId={proposal?.clientId}
            onUpdate={handleResumeUpdate}
          />
        )}
      </div>

      <ProposalContractsDialog
        open={contractsDialogOpen}
        onOpenChange={setContractsDialogOpen}
        title="Contratos da proposta"
        contracts={proposalContracts}
        isLoading={proposalContractsQuery.isLoading || isSyncingContracts}
        error={contractsError ? (contractsUnavailableReason ?? null) : null}
        onView={handleViewContract}
        onDownload={handleDownloadContract}
      />
    </motion.div>
  );
}

export function ProposalUpdatePage() {
  const params = useParams();
  const proposalId = params.id;

  if (!proposalId) {
    return null;
  }

  return (
    <UpdateProposalProvider proposalId={proposalId}>
      <Suspense
        fallback={
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        }
      >
        <ProposalUpdateContent />
      </Suspense>
    </UpdateProposalProvider>
  );
}
